// src/pages/LeaderboardPage.js
import React, { useState, useEffect, useMemo } from 'react';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { useAuthContext } from '../contexts/AuthContext';
import config from '../config';
import './LeaderboardPage.css';

const RANK_FILTERS = [
    { key: 'all', label: 'All Runners' },
    { key: 'Kipchoge', label: 'Kipchoge' },
    { key: 'Pro', label: 'Pro' },
    { key: 'Serious Runner', label: 'Serious Runner' },
    { key: 'Hobby Jogger', label: 'Hobby Jogger' },
];

export default function LeaderboardPage() {
    const { user, API_URL } = useAuthContext();
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortOrder, setSortOrder] = useState('desc');
    const [filterRank, setFilterRank] = useState('all');

    useEffect(() => {
        document.title = `Leaderboard | ${config.siteTitle}`;
    }, []);

    useEffect(() => {
        const fetchLeaderboard = async () => {
            try {
                const response = await fetch(`${API_URL}/api/leaderboard`, {
                    credentials: 'include',
                });
                if (response.ok) {
                    const data = await response.json();
                    setLeaderboard(data.leaderboard || []);
                }
            } catch (error) {
                console.error('Failed to fetch leaderboard:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchLeaderboard();
    }, [API_URL]);

    // Filter and sort the leaderboard
    const filteredLeaderboard = useMemo(() => {
        let result = [...leaderboard];

        // Apply filter
        if (filterRank !== 'all') {
            result = result.filter(u => u.rank_label === filterRank);
        }

        // Apply sort
        result.sort((a, b) => {
            if (sortOrder === 'desc') {
                return b.average_serious_score - a.average_serious_score;
            }
            return a.average_serious_score - b.average_serious_score;
        });

        return result;
    }, [leaderboard, filterRank, sortOrder]);

    const toggleSort = () => {
        setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    };

    const getRankClassName = (rankLabel) => {
        switch (rankLabel) {
            case 'Kipchoge': return 'rank-kipchoge';
            case 'Pro': return 'rank-pro';
            case 'Serious Runner': return 'rank-serious';
            default: return 'rank-hobby';
        }
    };

    return (
        <div className="leaderboard-page">
            <Nav />

            <main className="leaderboard-content">
                <div className="page-header">
                    <h2 className="page-title">Compare</h2>
                </div>

                {/* Filters */}
                <div className="leaderboard-controls">
                    <div className="filter-pills">
                        {RANK_FILTERS.map(filter => (
                            <button
                                key={filter.key}
                                className={`filter-pill ${filterRank === filter.key ? 'active' : ''}`}
                                onClick={() => setFilterRank(filter.key)}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Leaderboard Table */}
                {loading ? (
                    <div className="loading">Loading...</div>
                ) : filteredLeaderboard.length === 0 ? (
                    <div className="empty-state">
                        <p>No runners found matching your filter.</p>
                    </div>
                ) : (
                    <div className="leaderboard-table">
                        <div className="table-header">
                            <span className="col-rank">#</span>
                            <span className="col-user">Runner</span>
                            <span className="col-label">Rank</span>
                            <button className="col-score sortable" onClick={toggleSort}>
                                Score {sortOrder === 'desc' ? '↓' : '↑'}
                            </button>
                        </div>
                        <div className="table-body">
                            {filteredLeaderboard.map((entry, index) => (
                                <div
                                    key={entry.user_id}
                                    className={`table-row ${entry.user_id === user?.id ? 'is-current-user' : ''}`}
                                >
                                    <span className="col-rank">
                                        {index + 1}
                                    </span>
                                    <span className="col-user">
                                        {entry.username}
                                        {entry.user_id === user?.id && <span className="you-badge">You</span>}
                                    </span>
                                    <span className={`col-label ${getRankClassName(entry.rank_label)}`}>
                                        {entry.rank_label}
                                    </span>
                                    <span className="col-score">
                                        {entry.average_serious_score}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
}
